import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { userSchema } from "../../utils/Schema";
import { userServices } from "../../services/AllServices";
import { EMAIL_REGEX } from "../../utils/constants";

function AddEditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const originalReferralCode = useRef(data?.referralCode || "");

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d/.test(pastedText)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await userServices.getById({ id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const handleFinalSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        id: id || undefined,
        name: values.name.trim(),
        email: values.email,
        phoneNumber: values.phoneNumber,
        totalBids: values.totalBids,
        status: "active",
        referralCode: values.referralCode.trim(),
      };

      const response = id
        ? await userServices.edit(payload)
        : await userServices.add(payload);

      toast.success(response?.data?.message, { toastId: "success" });
      navigate(-1);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: userSchema,
    initialValues: {
      name: data?.name || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      totalBids: data?.totalBids?.toFixed(0) || 0,
      referralCode: data?.referralCode || "",
    },
    validate: (values) => {
      const errors = {};
      const email = values.email.trim();

      if (!email) {
        errors.email = "This field is required";
      } else if (!EMAIL_REGEX.test(email)) {
        errors.email = "Invalid email address";
      }
      return errors;
    },
    onSubmit: async (values) => {
      const trimmedCode = values.referralCode?.trim();

      // If referral code changed, confirm with user
      if (trimmedCode !== data?.referralCode) {
        setPendingValues(values); // store values to submit later
        setConfirmOpen(true); // open confirmation dialog
        return;
      }

      await handleFinalSubmit(values); // continue directly if no change
    },
  });

  const { values, touched, errors, handleChange, handleSubmit } = formik;
  const handleEmailChange = (event) => {
    const { value } = event.target;

    const trimmedValue = value.replace(/^\s+|\s+(?=\s)|\s+$/g, "");
    formik.setFieldValue("email", trimmedValue);
  };
  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} User`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    name="email"
                    value={formik.values.email}
                    onChange={handleEmailChange}
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    // required
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                {id ? (
                  <Grid item xs={6} md={6}>
                    <TextField
                      fullWidth
                      label="Bid Wallet Balance"
                      name="totalBids"
                      value={values.totalBids}
                      onChange={handleChange}
                      error={Boolean(touched.totalBids && errors.totalBids)}
                      helperText={touched.totalBids && errors.totalBids}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                ) : (
                  ""
                )}

                {id ? (
                  <Grid item xs={6} md={6}>
                    <TextField
                      fullWidth
                      label="Refer Code"
                      name="referralCode"
                      value={values?.referralCode || ""}
                      onChange={handleChange}
                      error={Boolean(
                        touched.referralCode && errors.referralCode
                      )}
                      helperText={touched.referralCode && errors.referralCode}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                ) : (
                  ""
                )}

                <Grid item xs={12} md={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} User
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>⚠️ Confirm Referral Code Change</DialogTitle>
        <DialogContent>
          Changing the referral code may affect this user or others who have
          already used the previous code. Are you sure you want to proceed with
          the change?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleFinalSubmit(pendingValues);
            }}
            color="primary"
            variant="contained"
          >
            Yes, Update It
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddEditUser;
